import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { authHeaders, StatusChip, fmtDate } from './instrShared';
import {
  Spinner, ArrowsClockwise, Warning, Hourglass, Flask, Package, Storefront, Wrench,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export const InstrDashboardTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/instrumentation/dashboard`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-24 text-center" data-testid="instr-dash-loading"><Spinner size={24} className="animate-spin text-[#0A2540] inline" /></div>;
  }
  if (!data) return null;

  const attention = [...data.maintenance.overdue_items.map((i) => ({ ...i, _s: 'overdue' })),
    ...data.maintenance.due_soon_items.map((i) => ({ ...i, _s: 'due_soon' }))];

  return (
    <div className="space-y-5" data-testid="instr-dashboard-tab">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => { setRefreshing(true); load(true); }}
          disabled={refreshing}
          data-testid="instr-dash-refresh"
          className="inline-flex items-center gap-2 h-8 px-3 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold uppercase tracking-wider text-[#0A2540] hover:bg-[#FCFAF6] transition"
        >
          {refreshing ? <Spinner size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />}
          Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <Kpi label="Stores" value={data.stores_count} icon={Storefront} />
        <Kpi label="Overdue Maintenance" value={data.maintenance.overdue_count} icon={Warning} alert={data.maintenance.overdue_count > 0} />
        <Kpi label="Due in 7 Days" value={data.maintenance.due_soon_count} icon={Hourglass} />
        <Kpi label="Quality Tests (30d)" value={data.quality.tests_30d} icon={Flask} sub={`${data.quality.failed_30d} failed`} alert={data.quality.failed_30d > 0} />
        <Kpi label="Stock Alerts" value={data.stock.out_count + data.stock.low_count} icon={Package} sub={`${data.stock.out_count} out · ${data.stock.low_count} low`} alert={data.stock.out_count > 0} />
      </div>

      {/* Per-store breakdown */}
      <Panel icon={Storefront} title="Stores Overview" testid="instr-dash-stores">
        {(!data.stores || data.stores.length === 0) ? (
          <Empty text="No stores found for your entity." />
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[640px]">
              <div className="grid grid-cols-[1.4fr_130px_130px_110px_160px] gap-2 items-center px-3 py-2 bg-[#FCFAF6] rounded-lg text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">
                <span>Store</span>
                <span>Overdue</span>
                <span>Due Soon</span>
                <span>Logs (30d)</span>
                <span>Last QC Result</span>
              </div>
              <div className="divide-y divide-[#EDE6D8]">
                {data.stores.map((s) => (
                  <div key={s.store_id} className="grid grid-cols-[1.4fr_130px_130px_110px_160px] gap-2 items-center px-3 py-2.5" data-testid={`instr-dash-store-${s.store_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1C1917] truncate">{s.store_name}</p>
                      {s.campus_name && <p className="text-[10px] text-[#78716C] truncate">{s.campus_name}</p>}
                    </div>
                    <span className={`text-sm font-mono font-semibold ${s.overdue_count > 0 ? 'text-[#B91C1C]' : 'text-[#0A2540]'}`}>{s.overdue_count}</span>
                    <span className={`text-sm font-mono font-semibold ${s.due_soon_count > 0 ? 'text-[#A16207]' : 'text-[#0A2540]'}`}>{s.due_soon_count}</span>
                    <span className="text-sm font-mono text-[#0A2540]">{s.logs_30d}</span>
                    {s.last_qc ? (
                      <div className="flex items-center gap-2">
                        <StatusChip status={s.last_qc.overall_pass ? 'pass' : 'fail'} label={s.last_qc.overall_pass ? 'PASS' : `FAIL (${s.last_qc.failed_count})`} />
                        <span className="text-[10px] font-mono text-[#78716C]">{fmtDate(s.last_qc.sample_date)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-[#78716C]">No test yet</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Maintenance attention list */}
        <Panel icon={Warning} title="Maintenance Attention" testid="instr-dash-attention">
          {attention.length === 0 ? (
            <Empty text="Nothing overdue or due soon. All components on schedule." />
          ) : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {attention.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 rounded-lg border border-[#EDE6D8] px-3 py-2" data-testid={`instr-dash-attn-${idx}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1C1917] truncate">{i.component} · {i.activity}</p>
                    <p className="text-[11px] text-[#78716C] truncate">{i.store_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <StatusChip status={i._s} />
                    <p className="text-[10px] font-mono text-[#78716C] mt-0.5">
                      {i._s === 'overdue' ? `${Math.abs(i.days_left)}d overdue` : `due ${fmtDate(i.due)}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Recent quality tests */}
        <Panel icon={Flask} title="Recent Quality Tests" testid="instr-dash-quality">
          {data.quality.recent_tests.length === 0 ? (
            <Empty text="No quality tests recorded yet." />
          ) : (
            <div className="space-y-2">
              {data.quality.recent_tests.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#EDE6D8] px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1C1917] truncate">{t.store_name || 'Store'}</p>
                    <p className="text-[11px] text-[#78716C]">{fmtDate(t.sample_date)} · {t.tested_by}</p>
                  </div>
                  <StatusChip status={t.overall_pass ? 'pass' : 'fail'} label={t.overall_pass ? 'PASS' : `FAIL (${t.failed_count})`} />
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Recent maintenance logs */}
      <Panel icon={Wrench} title="Recent Maintenance Logs" testid="instr-dash-logs">
        {data.recent_logs.length === 0 ? (
          <Empty text="No maintenance activity logged yet." />
        ) : (
          <div className="divide-y divide-[#EDE6D8]">
            {data.recent_logs.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1C1917] truncate">
                    {l.component} <span className="text-[#78716C] font-normal">· {l.action_type}</span>
                  </p>
                  <p className="text-[11px] text-[#78716C] truncate">{l.store_name} · {l.performed_by}</p>
                </div>
                <span className="text-xs font-mono text-[#78716C] shrink-0">{fmtDate(l.performed_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

function Kpi({ label, value, icon: Icon, sub, alert }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
        {Icon && <Icon size={16} weight="duotone" className="text-[#C9A35B] shrink-0" />}
      </div>
      <p className={`mt-2 text-2xl font-bold font-mono ${alert ? 'text-[#B91C1C]' : 'text-[#0A2540]'}`}>{value}</p>
      {sub && <p className="text-[11px] text-[#78716C] mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

function Panel({ icon: Icon, title, testid, children }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
          <Icon size={14} weight="fill" />
        </span>
        <p className="text-xs uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{title}</p>
      </div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-xs text-[#78716C] py-8 text-center">{text}</p>;
}
